import { useTranslation } from 'react-i18next'
import { Image, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { InsightTimeType, useBuilding, useFloor, useInsightsOverview, useLocation, useStore } from 'zens-store'
import {
  CalendarValue,
  Color,
  Help,
  Icon,
  Radius,
  Spacing,
  Text,
  Weekday,
  calendarValueToInsightsFromTo,
  displayTitleForCalendarValue,
} from 'zens-storybook'
import { useState } from 'react'
import Placeholder from '@dms/../assets/images/placeholder.png'

import { InfoBlock } from '../info'

import Statistic from './statistic'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.M,
  },
  title: {},
  address: {
    color: Color.metalGrey,
  },
  content: {
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
  },
  statistic: {
    flex: 1,
    marginHorizontal: Spacing.XS,
  },
  image: {
    width: 250,
    height: '100%',
    marginBottom: Spacing.S,
    marginLeft: Spacing.M,
    borderRadius: Radius.M,
  },
})

interface HelpInfo {
  index: number
  message: string
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
  type?: InsightTimeType
}

const InsightsSummary = (props: Props) => {
  const { companyId, buildingId, floorId = 0, period, weekdays, workingHours, style, ...otherProps } = props
  const [helpInfo, setHelpInfo] = useState<HelpInfo | undefined>(undefined)
  const { i18n } = useStore()
  const { t } = useTranslation()
  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const insights = useInsightsOverview(
    {
      companyId,
      buildingId,
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
    },
    { enabled: !!dates },
  )
  const building = useBuilding({ companyId, buildingId })
  const { name, src, locationId = 0 } = building.data || {}
  const location = useLocation({ companyId, locationId })
  const floor = useFloor({ companyId, floorId })

  const displayAddress = building.data?.displayAddress || location.data?.displayAddress
  const insight = floorId ? insights.data?.floors?.find((floor) => `${floor.id}` === `${floorId}`) : insights.data
  const numberOfWorkspaces = floorId ? floor.data?.numberOfWorkspaces : building.data?.numberOfWorkspaces || 0
  const numberOfUnused = insight?.numberOfUnusedWorkspaces || 0
  const periodTitle = displayTitleForCalendarValue(period, i18n.language, t)
  const peakValue = insight?.peak || 0
  const averageValue = insight?.avarage || 0

  function renderHelp() {
    if (!helpInfo) return
    const pointerStyle: ViewStyle = { marginLeft: `${12.5 + 25 * helpInfo.index}%` }
    return <Help message={helpInfo.message} icon={false} pointerStyle={pointerStyle} show />
  }

  return (
    <InfoBlock
      title={
        <Text level={3} style={styles.title}>
          {name} {floor.data ? `- ${floor.data.name || `${t('Floor')} ${floor.data.number}`}` : ''}
          {displayAddress && (
            <Text level={7} style={styles.address}>
              {'\n'}
              <Icon name="marker" size="S" /> {displayAddress}
            </Text>
          )}
        </Text>
      }
      style={[styles.container, style]}
      {...otherProps}
      right={<Image source={{ uri: src }} style={styles.image} defaultSource={Placeholder} />}
    >
      <View style={styles.content}>
        <Statistic
          value={`${numberOfWorkspaces ?? ' '}`}
          label={t('Total workspaces')}
          style={styles.statistic}
          onShowHelp={() =>
            helpInfo?.index === 0
              ? setHelpInfo(undefined)
              : setHelpInfo({
                  index: 0,
                  message: floorId
                    ? t('There are in total {{count}} workspaces on this floor.', {
                        count: numberOfWorkspaces,
                      })
                    : t('There are in total {{count}} workspaces in this building.', {
                        count: numberOfWorkspaces,
                      }),
                })
          }
        />
        <Statistic
          value={`${insight?.numberOfUnusedWorkspaces || 0}`}
          label={t('Unused workspaces')}
          style={styles.statistic}
          onShowHelp={() =>
            helpInfo?.index === 1
              ? setHelpInfo(undefined)
              : setHelpInfo({
                  index: 1,
                  message: t('{{numberOfUnused}} out of {{count}} workspaces were not used {{period}}.', {
                    numberOfUnused,
                    count: numberOfWorkspaces,
                    period: periodTitle,
                  }),
                })
          }
        />
        <Statistic
          value={`${peakValue} %`}
          label={t('Peak occupancy')}
          style={styles.statistic}
          onShowHelp={() =>
            helpInfo?.index === 2
              ? setHelpInfo(undefined)
              : setHelpInfo({
                  index: 2,
                  message: t(
                    'At the busiest moment {{period}}, we measured {{value}}% of the workspaces as occupied.',
                    {
                      period: periodTitle,
                      time: '',
                      value: peakValue,
                    },
                  ),
                })
          }
        />
        <Statistic
          value={`${averageValue} %`}
          label={t('Average occupancy')}
          style={styles.statistic}
          onShowHelp={() =>
            helpInfo?.index === 3
              ? setHelpInfo(undefined)
              : setHelpInfo({
                  index: 3,
                  message: t('The average percentage of workspaces that have been occupied {{period}} is {{value}}%.', {
                    value: averageValue,
                    period: periodTitle,
                  }),
                })
          }
        />
      </View>
      {renderHelp()}
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  return (
    <InfoBlock.Skeleton
      style={[styles.container, props.style]}
      title={
        <View>
          <Text.Skeleton width={200} level={1} />
          <Text.Skeleton />
        </View>
      }
      right={<Image source={Placeholder} style={styles.image} />}
    >
      <View style={styles.content}>
        <Statistic.Skeleton style={styles.statistic} label={t('Total workspaces')} />
        <Statistic.Skeleton style={styles.statistic} label={t('Unused workspaces')} />
        <Statistic.Skeleton style={styles.statistic} label={t('Peak occupancy')} />
        <Statistic.Skeleton style={styles.statistic} label={t('Average occupancy')} />
      </View>
    </InfoBlock.Skeleton>
  )
}
InsightsSummary.Skeleton = Skeleton
export default InsightsSummary
